import React from 'react';

import { css } from '@emotion/css';
import { TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { theme } from 'theme';

import { Button } from 'components/Button';
import { CenteredError } from 'components/Loaders';
import { Modal } from 'components/Modal';
import { DownloadButton, DownloadXSheetColumn, recordsToXSheet } from 'components/recipes/DownloadButton';
import { flattenExportColumns } from 'components/recipes/SearchFrame/utils';
import { Table } from 'components/Table';

import { useCoreApi } from 'hooks/useApi';

import { CloseableModalProps } from 'utils/asyncModal';

import { InventoryBriefCategoryConfig } from './briefCategories';

export interface InventoryBriefCategoryModalProps extends CloseableModalProps<void> {
  category: InventoryBriefCategoryConfig;
}

export function InventoryBriefCategoryModal({ category, onClose }: InventoryBriefCategoryModalProps) {
  const { data, error, isLoading } = useCoreApi(category.detailSchema);

  const getDownloadData = async () => {
    const exportColumns: Array<DownloadXSheetColumn<Any>> = flattenExportColumns(category.detailColumns).map(
      (column) => ({
        ...column,
        type: column.exportType ? column.exportType : 'string',
        value: (record: Any) => {
          const value =
            typeof column.exportValue === 'function' ? column.exportValue(record) : record[column.dataIndex as string];
          return column.exportType === 'number' ? (value as number) : (value as string);
        },
      }),
    );
    return recordsToXSheet(data?.items ?? [], exportColumns);
  };

  return (
    <Modal
      visible
      title={
        <div
          className={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 8px;
            margin-right: 40px;
          `}
        >
          <div>
            {category.title}
            <div
              className={css`
                ${theme.typography.pSmall};
                color: ${theme.colors.neutral[400]};
                font-weight: 400;
                padding-top: 4px;
              `}
            >
              {category.description}
            </div>
          </div>
          <DownloadButton
            permission={TenantUserPermission.AccessPlanningDashboardDownload}
            recordType={category.title.replace(/ /g, '')}
            disabled={isLoading || !!error || !data?.items?.length}
            getDownloadData={getDownloadData}
            tooltipPlacement="bottom"
          />
        </div>
      }
      width={1000}
      onCancel={() => onClose()}
      destroyOnClose
      footer={<Button onClick={() => onClose()}>Close</Button>}
    >
      {error ? (
        <CenteredError error={error} />
      ) : (
        <Table
          data={data?.items ?? []}
          pageSize={10}
          columns={category.detailColumns}
          rowKey={category.rowKey}
          isLoading={isLoading}
        />
      )}
    </Modal>
  );
}
